// Dashboard sub-form ops.
//
// `series` / `reference-line` / `reference-area` are positional
// children of `(cartesian …)`; `column` is a positional child of
// `(table …)`. They are NOT in the engine's `opMeta` — they register
// in `dashboardMeta` and are reachable only via their parent tile's
// child table (context restriction: a bare `(series …)` at top level
// is an `UnknownOpError`; a `(column …)` inside `(cartesian …)` is an
// `UnknownOpError` against the cartesian child table).
//
// Column slots (`:x`, `:y`, `:ref`) are explicit `(col …)` refs only
// (no positional-dataset / bare-symbol form). They
// are evaluated lazily via `resolveColKwarg` and stored as unresolved
// `ColumnRef`s (V-shape).

import { z } from 'zod';

import { SERIES_COLORS } from '#core/lib/formatters/colors.ts';

import { createOpTable, defineScope, type NameTable, type Scope, type SpecialOpMeta } from '../../op-meta.ts';
import { colRefArg } from './args.ts';
import { buildSpec, type DashboardCtx, dashboardMeta } from './dispatch.ts';
import { ALIGNS, AXIS_SIDES, CURVES, DISPLAY_FORMATS, REF_AXES, SERIES_TYPES, STROKES, X_SPACINGS } from './enums.ts';
import type {
  BandSpec,
  ColumnSpec,
  ReferenceAreaSpec,
  ReferenceLineSpec,
  SeriesSpec,
  XAxisSpec,
  YAxisSpec,
} from './value.ts';

// No sub-form takes positional args — every input is a kwarg.
const noPositional = z.tuple([]);

// A reference-marker coordinate: a plain number (value/numeric axis) or a
// timestamp string (ISO 8601 / date) for a time x-axis. The renderer
// normalizes a time-axis value through the same date→epoch-ms path the
// series x-values use, so the marker lands exactly on the data grid.
const refScalar = z.union([z.number(), z.string().min(1)]);

/**
 * `(series :type … :id … :label … :color … :axis? :curve? :x (col …) :y (col …) :stroke?)`
 * — one plotted series inside a `(cartesian …)` tile.
 */
export const series = z
  .object({
    name: z.literal('series'),
    params: noPositional,
    type: z.enum(SERIES_TYPES),
    id: z.string().min(1),
    label: z.string().min(1),
    color: z.enum(SERIES_COLORS),
    axis: z.enum(AXIS_SIDES).default('left'),
    curve: z.enum(CURVES).default('smooth'),
    x: colRefArg,
    y: colRefArg,
    z: colRefArg.optional(),
    stroke: z.enum(STROKES).optional(),
  })
  .describe(
    'One plotted series inside `(cartesian …)`. `:x`/`:y` are explicit `(col ds colname)` refs; ' +
      'each series names its own dataset, so multiple series may come from DIFFERENT datasets as long as ' +
      'their `:x` columns share the same domain/kind (e.g. all epoch-ms timestamps). ' +
      '`:axis` (default left; use right for a series whose range/unit ' +
      "differs so it isn't flattened), `:curve` (default smooth, line/area only; prefer " +
      'linear for sampled data), `:stroke` sets the line style to disambiguate overlapping lines. ' +
      '`:z (col …)` is an optional third channel that sizes a `scatter` point (bubble chart); ignored for other types.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: {
      expr: '(series :type line :id rev :label "Revenue" :color blue :x (col w timestamp) :y (col w revenue))',
    },
    apply: (data, ctx: DashboardCtx): Promise<SeriesSpec> =>
      buildSpec('series-spec', data, ctx, [
        { key: 'type', mode: 'copy' },
        { key: 'id', mode: 'copy' },
        { key: 'label', mode: 'copy' },
        { key: 'color', mode: 'copy' },
        { key: 'axis', mode: 'copy' },
        { key: 'curve', mode: 'copy' },
        { key: 'x', mode: 'col' },
        { key: 'y', mode: 'col' },
        { key: 'z', mode: 'optional-col' },
        { key: 'stroke', mode: 'optional' },
      ]),
  });

/** `(reference-line :axis x|y :value N :label? :style? :color?)`. */
export const referenceLine = z
  .object({
    name: z.literal('reference-line'),
    params: noPositional,
    axis: z.enum(REF_AXES),
    value: refScalar,
    label: z.string().min(1).optional(),
    style: z.enum(STROKES).optional(),
    color: z.enum(SERIES_COLORS).optional(),
  })
  .describe(
    'A guide line inside `(cartesian …)` at a fixed `:value` on the `:axis`. ' +
      '`:axis y` = horizontal line at a y value (target/limit/threshold — the common case), ' +
      '`:value` is a number. `:axis x` = vertical line at an x position: on a time x-axis ' +
      'pass `:value` as an ISO/date string (e.g. "2025-06-29"); on a numeric x-axis a number. ' +
      'The marker always renders even if just outside the data range. Use `:style dashed` to ' +
      'set it apart from data series.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(reference-line :axis y :value 10000 :label "Target" :style dashed)' },
    apply: (data, ctx: DashboardCtx): Promise<ReferenceLineSpec> =>
      buildSpec('reference-line-spec', data, ctx, [
        { key: 'axis', mode: 'copy' },
        { key: 'value', mode: 'copy' },
        { key: 'label', mode: 'optional' },
        { key: 'style', mode: 'optional' },
        { key: 'color', mode: 'optional' },
      ]),
  });

/** `(reference-area :axis x|y :start N :end N :label? :color?)`. */
export const referenceArea = z
  .object({
    name: z.literal('reference-area'),
    params: noPositional,
    axis: z.enum(REF_AXES),
    start: refScalar,
    end: refScalar,
    label: z.string().min(1).optional(),
    color: z.enum(SERIES_COLORS).optional(),
  })
  .describe(
    'A shaded band inside `(cartesian …)` between `:start` and `:end` on the `:axis`. ' +
      '`:axis y` shades a value range (e.g. a normal-operating band) — numbers. `:axis x` ' +
      'shades an x-position range: on a time x-axis pass `:start`/`:end` as ISO/date strings ' +
      '(e.g. "2025-06-01" … "2025-06-30"); on a numeric x-axis numbers. `:start` < `:end`.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(reference-area :axis x :start 5 :end 10 :label "Window")' },
    apply: (data, ctx: DashboardCtx): Promise<ReferenceAreaSpec> =>
      buildSpec('reference-area-spec', data, ctx, [
        { key: 'axis', mode: 'copy' },
        { key: 'start', mode: 'copy' },
        { key: 'end', mode: 'copy' },
        { key: 'label', mode: 'optional' },
        { key: 'color', mode: 'optional' },
      ]),
  });
/**
 * `(band :x (col …) :lower (col …) :upper (col …) :axis? :color? :label?)`
 * — a shaded min/max band, plotted as a range area against the shared
 * `:x` domain. Independent of any `(series …)`.
 */
export const band = z
  .object({
    name: z.literal('band'),
    params: noPositional,
    x: colRefArg,
    lower: colRefArg,
    upper: colRefArg,
    axis: z.enum(AXIS_SIDES).default('left'),
    color: z.enum(SERIES_COLORS).optional(),
    label: z.string().min(1).optional(),
  })
  .describe(
    'A shaded min/max band inside `(cartesian …)` — use for confidence/percentile/min-max ' +
      'envelopes around a trend. `:x`/`:lower`/`:upper` are explicit `(col ds colname)` refs ' +
      '(they may come from a different dataset than the series, as long as `:x` shares the ' +
      'chart x domain); the band fills between `:lower` and `:upper` along `:x`. `:axis` ' +
      '(default left) — use right only if the band is on the secondary y-axis scale.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: {
      expr: '(band :x (col w ts) :lower (col w lo) :upper (col w hi) :color blue :label "p10–p90")',
    },
    apply: (data, ctx: DashboardCtx): Promise<BandSpec> =>
      buildSpec('band-spec', data, ctx, [
        { key: 'x', mode: 'col' },
        { key: 'lower', mode: 'col' },
        { key: 'upper', mode: 'col' },
        { key: 'axis', mode: 'copy' },
        { key: 'color', mode: 'optional' },
        { key: 'label', mode: 'optional' },
      ]),
  });
/** `(x-axis :label? :spacing?)` — x-axis configuration inside `(cartesian …)`. */
export const xAxis = z
  .object({
    name: z.literal('x-axis'),
    params: noPositional,
    label: z.string().min(1).optional(),
    spacing: z.enum(X_SPACINGS).optional(),
  })
  .describe(
    'Configures the `(cartesian …)` shared x-axis. `:label` sets the axis title. `:spacing` ' +
      '(default auto) controls how x values are laid out across the ' +
      'plot width: discrete gives one equal-width slot per row so bars align to ticks; ' +
      'continuous keeps proportional spacing on numeric/time axes. Auto resolves to discrete ' +
      'iff any series is a bar — set explicitly to override (e.g., continuous for irregular ' +
      'time data with bars). The x domain/kind is inferred from the ' +
      'series `:x` columns, not set here.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(x-axis :label "Time" :spacing discrete)' },
    apply: (data, ctx: DashboardCtx): Promise<XAxisSpec> =>
      buildSpec('x-axis-spec', data, ctx, [
        { key: 'label', mode: 'optional' },
        { key: 'spacing', mode: 'optional' },
      ]),
  });

/**
 * `(y-axis :side? left|right :label?)` — y-axis configuration inside
 * `(cartesian …)`. `:side` selects which y-axis (default left).
 */
export const yAxis = z
  .object({
    name: z.literal('y-axis'),
    params: noPositional,
    side: z.enum(AXIS_SIDES).default('left'),
    label: z.string().min(1).optional(),
  })
  .describe(
    'Configures a `(cartesian …)` y-axis. `:side` (default left) picks WHICH ' +
      'y-axis to label/title — pair `:side right` with series that set `:axis right` so the ' +
      'secondary scale (for a mismatched range/unit) gets its own title. Add one `(y-axis …)` ' +
      'per side you want to label; `:label` sets that axis title.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(y-axis :side left :label "Revenue")' },
    apply: (data, ctx: DashboardCtx): Promise<YAxisSpec> =>
      buildSpec('y-axis-spec', data, ctx, [
        { key: 'side', mode: 'copy' },
        { key: 'label', mode: 'optional' },
      ]),
  });

/** `(column :ref (col …) :label … :format … :align? :color-by-value?)`. */
export const column = z
  .object({
    name: z.literal('column'),
    params: noPositional,
    ref: colRefArg,
    label: z.string().min(1),
    format: z.enum(DISPLAY_FORMATS).optional(),
    align: z.enum(ALIGNS).optional(),
    'color-by-value': z.boolean().optional(),
    sort: z.enum(['asc', 'desc']).optional(),
    sortable: z.boolean().optional(),
  })
  .describe('One column inside `(table …)`. `:ref` is an explicit `(col ds colname)`.')
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(column :ref (col wells revenue) :label "Revenue" :format currency)' },
    apply: (data, ctx: DashboardCtx): Promise<ColumnSpec> =>
      buildSpec('column-spec', data, ctx, [
        { key: 'ref', mode: 'col' },
        { key: 'label', mode: 'copy' },
        { key: 'format', mode: 'optional' },
        { key: 'align', mode: 'optional' },
        { key: 'colorByValue', from: 'color-by-value', mode: 'optional' },
        { key: 'sort', mode: 'optional' },
        { key: 'sortable', mode: 'optional' },
      ]),
  });

/** Sub-forms legal as positional children of `(cartesian …)`. */
export const cartesianChildScope: Scope<SpecialOpMeta> = defineScope({
  name: 'Cartesian sub-forms',
  description: 'Positional children of `(cartesian …)` — at least one `series` is required.',
  registry: dashboardMeta,
  schemas: [series, referenceLine, referenceArea, band, xAxis, yAxis],
});

/** Sub-forms legal as positional children of `(table …)`. */
export const tableChildScope: Scope<SpecialOpMeta> = defineScope({
  name: 'Table sub-forms',
  description: 'Positional children of `(table …)` — at least one `column` is required.',
  registry: dashboardMeta,
  schemas: [column],
});

/** Dispatch tables — name → schema, scoped per parent tile. */
export const CARTESIAN_CHILD_TABLE: NameTable = createOpTable([
  series,
  referenceLine,
  referenceArea,
  band,
  xAxis,
  yAxis,
]);
export const TABLE_CHILD_TABLE: NameTable = createOpTable([column]);
